import type { ModelName } from '@pigello/pigello-matrix';
import { X } from 'lucide-react';
import type { JsonFilter } from '@/hooks/use-table-filter/types';
import { cn } from '@/lib/cn';
import { useGetCount } from '@/requests/hooks';
import type { QueryParams } from '@/requests/types';
import { Icons } from '../icons';
import { Badge } from '../ui/badge';
import type { Filter, FilterEnumChoice } from './constants';
import { getFilterIsActive } from './utils';

const contractModelNames: { [key in ModelName]?: boolean } = {
  apartmentcontract: true,
  industrialpremisescontract: true,
  parkingspotcontract: true,
  outdoorsectioncontract: true,
};

export default function EnumFilter({
  choice,
  filter,
  filterVariant,
  toggleFilter,
  modelName,
  overrideUrl,
  queryParamFilters,
  isFetchingTableData,
  enableGlobalFilters,
}: {
  choice: FilterEnumChoice;
  filter: JsonFilter | null;
  filterVariant: Filter;
  toggleFilter: (
    filterVariant: Filter,
    choice: FilterEnumChoice,
    remove: boolean
  ) => void;
  modelName: ModelName;
  overrideUrl?: string;
  queryParamFilters?: QueryParams['filters'];
  isFetchingTableData: boolean;
  enableGlobalFilters?: boolean;
}) {
  const { data: count, isPending: isLoadingCount } = useGetCount({
    modelName,
    overrideUrl,
    filters: {
      ...queryParamFilters,
      ...(contractModelNames?.[modelName] && {
        is_draft: { __in: false },
      }),
      ...choice.filters.reduce((acc, cur) => {
        const externalFieldName =
          cur.fieldName ??
          choice.overrideExternalFieldName ??
          filterVariant.externalFieldName;
        const prevForField = acc[externalFieldName] ?? {};
        const updatedForField = {
          ...prevForField,
          [cur.operator]: cur.value,
        };
        acc[externalFieldName] = updatedForField;
        return acc;
      }, {} as JsonFilter),
    },
    enabled: !isFetchingTableData,
    enableGlobalFilters,
  });

  const choiceIsSelected = getFilterIsActive(filterVariant, choice, filter);

  return (
    <button
      type='button'
      onClick={() => toggleFilter(filterVariant, choice, choiceIsSelected)}
      className={cn('flex items-center p-0')}
      key={choice.title}
    >
      <Badge className='rounded-full text-xs' variant={choice.badgeVariant}>
        {choice.title}{' '}
        {isLoadingCount ? (
          <Icons.loader className='ml-1 size-3.5 animate-spin' />
        ) : (
          (count ?? 0)
        )}
        {choiceIsSelected && <X className='ml-1 size-3.5' />}
      </Badge>
    </button>
  );
}
